import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Compass, Square } from "lucide-react";
import {
  api,
  type Exploration,
  type ExploreNode,
  type RobotReview,
} from "@/lib/api";
import { dateTime } from "@/lib/format";
import {
  AudioPlayer,
  Badge,
  Button,
  Card,
  Dialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from "@/components/ui";

// --- the explorer ----------------------------------------------------------
//
// A test mailbox, its PIN and a provider go in; the worker calls the box
// step by step, Whisper listens, the language model chooses the keys, and a
// dialer script comes out that can be kept under Definitionen.

const providers = [
  { id: "a1", label: "A1" },
  { id: "magenta", label: "Magenta" },
  { id: "tmobile", label: "T-Mobile (Magenta, englisch)" },
  { id: "drei", label: "Drei" },
  { id: "bob", label: "bob" },
  { id: "hot", label: "HoT" },
];

const stateTone: Record<Exploration["state"], "neutral" | "info" | "success" | "danger" | "warning"> = {
  queued: "neutral",
  running: "info",
  done: "success",
  failed: "danger",
  cancelled: "warning",
};

const stateLabel: Record<Exploration["state"], string> = {
  queued: "wartet",
  running: "läuft",
  done: "fertig",
  failed: "fehlgeschlagen",
  cancelled: "abgebrochen",
};

/** The "Skripte erstellen" tab of the robot page. */
export function ExploreTab() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<number | null>(null);

  // A script belongs to no company: it is the way into one provider's
  // mailbox, and any number on that network will do to find it.
  const list = useQuery({
    queryKey: ["explorations"],
    queryFn: () => api<Exploration[]>("/api/admin/robot/explorations?limit=50"),
    refetchInterval: 5_000,
  });

  return (
    <>
      <p className="mb-4 max-w-3xl text-sm muted">
        Eine beliebige Mobilboxnummer samt PIN eintragen: der Roboter wählt
        sich selbst durch die Mobilbox dieses Providers und schreibt das
        Dialer-Skript dazu. Jeder Schritt ist ein Anruf, jede Antwort der Box
        wird transkribiert. Das Skript gilt für den Provider, nicht für eine
        Firma.
      </p>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <StartForm onStarted={(id) => setSelected(id)} />
        <Card>
          {list.isLoading ? (
            <Spinner />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Gestartet</Th>
                  <Th>Provider</Th>
                  <Th>Nummer</Th>
                  <Th>Zustand</Th>
                  <Th>Anrufe</Th>
                  <Th>Ergebnis</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {(list.data ?? []).map((x) => (
                  <tr
                    key={x.id}
                    className={x.id === selected ? "bg-[var(--accent-soft)]" : undefined}
                  >
                    <Td className="whitespace-nowrap muted">{dateTime(x.createdAt)}</Td>
                    <Td>{x.provider}</Td>
                    <Td className="tabular-nums">{x.msisdn}</Td>
                    <Td>
                      <Badge tone={stateTone[x.state]}>{stateLabel[x.state]}</Badge>
                      {x.live && <span className="ml-2 text-xs muted">aktiv</span>}
                    </Td>
                    <Td className="tabular-nums">{x.calls}</Td>
                    <Td className="text-xs">
                      {x.scriptId ? (
                        <Link to="/definitions" className="underline">
                          Skript #{x.scriptId}
                        </Link>
                      ) : x.script ? (
                        x.verify === "ok" ? "Skript verifiziert" : `Skript, Verifikation: ${x.verify || "offen"}`
                      ) : (
                        x.reason || "—"
                      )}
                    </Td>
                    <Td className="text-right">
                      <Button size="sm" onClick={() => setSelected(x.id)}>
                        Details
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {(list.data ?? []).length === 0 && !list.isLoading && (
            <Empty title="Noch kein Lauf" hint="Links Provider, Nummer und PIN eintragen und starten." />
          )}
        </Card>
      </div>
      {selected !== null && (
        <ExplorationDetail
          id={selected}
          onClose={() => setSelected(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ["explorations"] })}
        />
      )}
    </>
  );
}

function StartForm({ onStarted }: { onStarted: (id: number) => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({
    provider: "a1",
    msisdn: "",
    pin: "",
    audioKey: "",
    maxCalls: 25,
    probes: "number,pin",
  });
  const start = useMutation({
    mutationFn: () =>
      api<Exploration>("/api/admin/robot/explorations", {
        method: "POST",
        body: draft,
      }),
    onSuccess: (x) => {
      setDraft((d) => ({ ...d, pin: "" }));
      void qc.invalidateQueries({ queryKey: ["explorations"] });
      onStarted(x.id);
    },
  });

  return (
    <Card>
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          start.mutate();
        }}
      >
        <Notice>
          Nur die eingetragene Testbox wird angerufen. PIN ändern, löschen,
          deaktivieren und Rufumleitung sind gesperrt; ein Sperrhinweis der
          Box beendet den Lauf sofort. Die PIN wird verschlüsselt hinterlegt
          und nach dem Lauf gelöscht.
        </Notice>
        <Field label="Provider" hint="Der tatsächliche Betreiber der Mailbox. Nummern sind portierbar, die Vorwahl sagt es nicht — wähle den Betreiber, bei dem die Box liegt.">
          <Select
            value={draft.provider}
            onChange={(e) => setDraft({ ...draft, provider: e.target.value })}
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Mobilboxnummer" hint="Eine Testbox dieses Providers; sie muss zu keiner Person gehören.">
          <Input
            value={draft.msisdn}
            placeholder="43664 1234567"
            onChange={(e) => setDraft({ ...draft, msisdn: e.target.value })}
          />
        </Field>
        <Field label="Mobilbox-PIN">
          <Input
            type="password"
            autoComplete="off"
            value={draft.pin}
            onChange={(e) => setDraft({ ...draft, pin: e.target.value })}
          />
        </Field>
        <Field
          label="Ansage (Aufnahme)"
          hint="Name einer gerenderten Aufnahme; leer: der Worker spricht einen Standardsatz per Sprachsynthese ein. Ein Testton reicht nicht — eine echte Box nimmt nur Sprache als Begrüßung an."
        >
          <Input
            value={draft.audioKey}
            onChange={(e) => setDraft({ ...draft, audioKey: e.target.value })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Höchstens Anrufe">
            <Input
              type="number"
              min={3}
              max={60}
              value={draft.maxCalls}
              onChange={(e) => setDraft({ ...draft, maxCalls: Number(e.target.value) })}
            />
          </Field>
          <Field label="Proben" hint="Fehleransagen aufnehmen">
            <Select
              value={draft.probes}
              onChange={(e) => setDraft({ ...draft, probes: e.target.value })}
            >
              <option value="number,pin">Nummer und PIN</option>
              <option value="number">nur Nummer</option>
              <option value="none">keine</option>
            </Select>
          </Field>
        </div>
        {start.error && <ErrorNote>{(start.error as Error).message}</ErrorNote>}
        <Button type="submit" variant="primary" disabled={start.isPending}>
          <Compass className="size-4" aria-hidden /> Skript erstellen
        </Button>
      </form>
    </Card>
  );
}

function ExplorationDetail({
  id,
  onClose,
  onSaved,
}: {
  id: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const { data: x, isLoading } = useQuery({
    queryKey: ["exploration", id],
    queryFn: () => api<Exploration>(`/api/admin/robot/explorations/${id}`),
    refetchInterval: (q) => {
      const s = q.state.data?.state;
      return s === "queued" || s === "running" ? 3_000 : false;
    },
  });
  useEffect(() => {
    if (x && !name) setName(`${x.provider.toUpperCase()} automatisch ${x.msisdn.slice(-4)}`);
  }, [x, name]);

  const cancel = useMutation({
    mutationFn: () => api<void>(`/api/admin/robot/explorations/${id}/cancel`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["exploration", id] }),
  });
  const save = useMutation({
    mutationFn: () =>
      api<{ scriptId: number }>(`/api/admin/robot/explorations/${id}/save-script`, {
        method: "POST",
        body: { name },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["exploration", id] });
      void qc.invalidateQueries({ queryKey: ["dialer-scripts"] });
      onSaved();
    },
  });

  const nodes: ExploreNode[] = x ? (x.nodes ?? []) : [];
  const running = x?.state === "queued" || x?.state === "running";

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={x ? `Lauf #${x.id} · ${x.provider} · ${x.msisdn}` : "Lauf"}
    >
      {isLoading || !x ? (
        <Spinner />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge tone={stateTone[x.state]}>{stateLabel[x.state]}</Badge>
            <span>{x.calls} Anrufe</span>
            {x.status && <span className="muted">{x.status}</span>}
            {x.reason && <span className="muted">{x.reason}</span>}
            {x.verify && (
              <Badge tone={x.verify === "ok" ? "success" : "danger"}>
                Verifikation {x.verify}
              </Badge>
            )}
            {running && (
              <Button size="sm" onClick={() => cancel.mutate()} disabled={cancel.isPending || x.cancelRequested}>
                <Square className="size-4" aria-hidden /> {x.cancelRequested ? "wird beendet" : "Abbrechen"}
              </Button>
            )}
          </div>

          <div className="text-sm">
            <span className="muted">Pfad: </span>
            {(x.path ?? []).map((a, i) => (
              <span key={i} className="mr-1">
                <code>{actionLabel(a)}</code>
                {i < x.path.length - 1 && " → "}
              </span>
            ))}
          </div>

          <Table>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Art</Th>
                <Th>Gehört</Th>
                <Th>Mithören</Th>
                <Th>Entscheidung</Th>
              </tr>
            </thead>
            <tbody>
              {nodes.map((n) => (
                <tr key={n.index}>
                  <Td className="tabular-nums">{n.index + 1}</Td>
                  <Td className="text-xs">
                    {n.kind}
                    {n.exitCode !== 0 && <span className="ml-1 muted">exit {n.exitCode}</span>}
                    {n.features?.beepHz ? <span className="ml-1 muted">Aufnahmeton {n.features.beepHz} Hz</span> : null}
                    {n.features?.promptHz ? <span className="ml-1 muted">Eingabeton {n.features.promptHz} Hz</span> : null}
                    {n.features?.hitCap ? <span className="ml-1 muted">Box redete noch</span> : null}
                    {n.category === "fragment" ? (
                      <span className="ml-1 muted" title="Unter einer Sekunde Ton; wird mit längerer Pause noch einmal angehört.">
                        Fragment
                      </span>
                    ) : null}
                    {n.listen === "long" ? <span className="ml-1 muted">lange gehört</span> : null}
                    {n.pruned ? (
                      <span className="ml-1 muted" title="Die Box hat diesen Schritt abgelehnt; er wird nicht mehr wiederholt.">
                        verworfen
                      </span>
                    ) : null}
                  </Td>
                  <Td className="text-xs max-w-2xl">{n.transcript || <span className="muted">—</span>}</Td>
                  <Td>
                    {n.session ? (
                      <AudioPlayer src={`/api/admin/robot/explorations/${id}/nodes/${n.index}/audio`} />
                    ) : (
                      <span className="muted text-xs">—</span>
                    )}
                  </Td>
                  <Td className="text-xs">
                    {n.decision ? (
                      <>
                        <code>{decisionLabel(n.decision)}</code>{" "}
                        <span className="muted">{n.decision.reason}</span>
                      </>
                    ) : (
                      n.category || "—"
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          {nodes.length === 0 && <Empty title="Noch kein Anruf" />}

          {x.script && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-end gap-2">
                {x.scriptId ? (
                  <Notice tone="success">
                    Automatisch als Dialer-Skript #{x.scriptId} gespeichert (Provider, Datum, Uhrzeit) — unter{" "}
                    <Link to="/definitions" className="underline">
                      Definitionen
                    </Link>{" "}
                    einer Person zuweisen.
                  </Notice>
                ) : (
                  <>
                    <Field label="Als Dialer-Skript speichern unter">
                      <Input value={name} onChange={(e) => setName(e.target.value)} />
                    </Field>
                    <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending || !name}>
                      Speichern
                    </Button>
                  </>
                )}
              </div>
              {save.error && <ErrorNote>{(save.error as Error).message}</ErrorNote>}
              {(x.samples ?? []).length > 0 && (
                <p className="text-xs muted">
                  Neue Samples: {(x.samples ?? []).map((s) => s.path).join(", ")}
                </p>
              )}
              <pre className="max-h-72 overflow-auto rounded border p-3 text-xs">{x.script}</pre>
            </div>
          )}

          {x.log && (
            <details>
              <summary className="cursor-pointer text-sm muted">Protokoll</summary>
              <pre className="max-h-60 overflow-auto rounded border p-3 text-xs">{x.log}</pre>
            </details>
          )}

          <div className="flex justify-end">
            <Button onClick={onClose}>Schließen</Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function actionLabel(a: Exploration["path"][number]): string {
  switch (a.kind) {
    case "dial":
      return "Anruf";
    case "keys":
      return a.keys.replace("$NUMBER#", "NUMMER#").replace("$PIN#", "PIN#").replace("$PIN", "PIN");
    case "beep":
      return `Signalton ${a.hz ?? ""} Hz`;
    case "greeting":
      return "Ansage + #";
    default:
      return a.kind;
  }
}

function decisionLabel(d: NonNullable<ExploreNode["decision"]>): string {
  if (d.done) return "fertig";
  if (d.needs_greeting) return "Ansage sprechen";
  if (d.expects_beep) return "auf Signalton warten";
  if (d.key) return `Taste ${d.key.replace("$NUMBER#", "NUMMER#").replace("$PIN#", "PIN#")}`;
  return d.intent || "warten";
}

// --- reviews: what did the box say? ----------------------------------------

export const verdictTone: Record<string, "neutral" | "success" | "danger" | "warning" | "info"> = {
  confirmed: "success",
  rejected: "danger",
  heard: "info",
  unknown: "warning",
};

export const verdictLabel: Record<string, string> = {
  confirmed: "bestätigt",
  rejected: "abgewiesen",
  heard: "gehört",
  unknown: "unklar",
};

/** One review, compact: verdict, summary, the transcripts on request. */
export function ReviewLine({ r }: { r: RobotReview }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="muted whitespace-nowrap">{dateTime(r.createdAt)}</span>
        <Badge tone={r.kind === "listen" ? "info" : "neutral"}>
          {r.kind === "listen" ? "Abhör-Anruf" : "Transkript"}
        </Badge>
        {r.state === "done" ? (
          <Badge tone={verdictTone[r.verdict] ?? "neutral"}>{verdictLabel[r.verdict] ?? r.verdict}</Badge>
        ) : (
          <Badge tone={r.state === "failed" ? "danger" : "info"}>
            {r.state === "failed" ? "fehlgeschlagen" : r.state === "running" ? "läuft" : "wartet"}
          </Badge>
        )}
        <span className="muted">{r.summary || r.error}</span>
        {r.transcripts.length > 0 && (
          <button type="button" className="text-xs underline" onClick={() => setOpen(!open)}>
            {open ? "weniger" : `${r.transcripts.length} Aufnahmen`}
          </button>
        )}
      </div>
      {open && (
        <ul className="mt-1 ml-4 list-disc text-xs">
          {r.transcripts.map((t) => (
            <li key={t.file}>
              <span className="muted">
                {t.file} ({t.seconds.toFixed(1)} s, {t.category})
              </span>
              : {t.text || "—"}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A button that asks for a transcript review of one thing and lists what came of earlier ones. */
export function ReviewButton({
  path,
  queryKey,
  label = "Prüfen",
}: {
  path: string; // e.g. /api/admin/calls/12/review
  queryKey: readonly unknown[];
  label?: string;
}) {
  const qc = useQueryClient();
  const req = useMutation({
    mutationFn: () => api<RobotReview>(path, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey }),
  });
  return (
    <>
      <Button size="sm" onClick={() => req.mutate()} disabled={req.isPending} title="Was hat die Mobilbox gesagt?">
        {label}
      </Button>
      {req.error && (
        <span className="ml-2 text-xs text-[var(--danger)]">{(req.error as Error).message}</span>
      )}
    </>
  );
}

/** Per announcement put on a mailbox by the robot: what did the box say? */
export function AnnouncementReview({ id }: { id: number }) {
  const { data } = useQuery({
    queryKey: ["announcement-reviews", id],
    queryFn: () => api<RobotReview[]>(`/api/admin/robot/reviews?announcementId=${id}&limit=3`),
    refetchInterval: (q) => (q.state.data?.some((r) => r.state === "queued" || r.state === "running") ? 4_000 : false),
  });
  const last = data?.[0];
  return (
    <span className="mr-2 inline-flex items-center gap-2">
      {last && last.state === "done" && (
        <Badge tone={verdictTone[last.verdict] ?? "neutral"} >
          {verdictLabel[last.verdict] ?? last.verdict}
        </Badge>
      )}
      <ReviewButton path={`/api/admin/announcements/${id}/review`} queryKey={["announcement-reviews", id]} label={last ? "Erneut prüfen" : "Prüfen"} />
    </span>
  );
}
